import { useSearchParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { SearchInput } from '@/components/ui/Input'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useContracts } from '@/hooks/useProOps'
import { formatTHB } from '@/lib/utils'
import type { Contract } from '@/lib/pocketbase'

export function ContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading, error } = useContracts({ search, page, perPage: 20 })

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  return (
    <div>
      <PageHeader
        title="สัญญา"
        description="เรียกดูสัญญาที่ลงนามทั้งหมด"
      />

      <div className="mb-6">
        <SearchInput
          placeholder="ค้นหาด้วยเลขที่สัญญา..."
          defaultValue={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {isLoading && <LoadingState />}

      {error && (
        <Card className="p-6 text-center">
          <Icon icon="solar:danger-circle-line-duotone" className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">โหลดสัญญาไม่สำเร็จ</h3>
        </Card>
      )}

      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon icon="solar:document-add-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">ไม่พบสัญญา</h3>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((contract) => (
              <ContractCard key={contract.id} contract={contract} />
            ))}
          </div>
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

function ContractCard({ contract }: { contract: Contract }) {
  const tender = contract.expand?.tender_id as any
  const company = contract.expand?.company_id as any

  const contractNumber =
    (contract as any).contract_number ||
    (contract as any).contract_no ||
    contract.id.slice(0, 8)
  const amount =
    (contract as any).total_price ||
    (contract as any).value_amount_satang ||
    null

  return (
    <Link to={`/tenders/${tender?.id || contract.tender_id || contract.id}`}>
      <Card hover>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="success" size="sm">
                <Icon icon="solar:document-add-line-duotone" className="w-3 h-3" />
                สัญญา
              </Badge>
              <span className="font-mono text-sm text-slate-500">
                #{contractNumber}
              </span>
            </div>
            {tender && (
              <p className="font-display font-medium text-slate-900 mt-2 line-clamp-2">
                {tender.project_name || tender.title_th || 'ไม่ทราบโครงการ'}
              </p>
            )}
            {company && (
              <p className="text-sm text-slate-500 mt-1">
                {company.name ||
                  company.name_th ||
                  company.name_en ||
                  'ไม่ทราบผู้รับจ้าง'}
              </p>
            )}
          </div>
          <div className="text-right">
            {amount && (
              <p className="font-medium text-slate-900">{formatTHB(amount)}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              {contract.sign_date ? (
                <DateDisplay date={contract.sign_date} format="relative" />
              ) : (
                '-'
              )}
            </p>
          </div>
        </div>
      </Card>
    </Link>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="flex items-center gap-4">
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-slate-200 rounded" />
              <div className="h-4 w-48 bg-slate-200 rounded" />
            </div>
            <div className="h-4 w-24 bg-slate-200 rounded" />
          </div>
        </Card>
      ))}
    </div>
  )
}
